/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 04 de 05 de 2026
 */
import {useEffect, useLayoutEffect} from "react";
import {AUTHENTICATION_MODEL, STEPS_UPDATE_DEVICE} from "../Model/Model";
import {errorMsg} from "../../../../Apis/functions";
import {
    CreateDepartment,
    CreateEmployee, GetDeviceDetails,
    UpdateDeviceApi
} from "../../../../reduxToolkit/Devices/DevicesApi";
import {toast} from "react-toastify";
import routes from "../../../../Routes/routes";
import {isValid} from "../Model/DeviceValidator";

/**
 * Custom hook used to manage device update workflow.
 *
 * This hook handles:
 * - Step initialization.
 * - Device detail retrieval.
 * - Device update requests.
 * - Employee and department relationship creation.
 * - 1FA and 2FA assignment handling.
 * - Form validation.
 * - Device state synchronization.
 * - Navigation after successful update.
 *
 * @function UpdateDevicesApp
 *
 * @param {Object} props - Hook parameters.
 * @param {Function} props.setSteps - Updates step list.
 * @param {Function} props.handleError - Global API error handler.
 * @param {Array<Object>} props.deviceCustomAccess - Employee assignments for 1FA.
 * @param {Function} props.setDeviceCustomAccess - Updates 1FA employee assignments.
 * @param {Array<Object>} props.deviceDepartment - Department assignments for 1FA.
 * @param {Function} props.setDeviceDepartment - Updates 1FA department assignments.
 * @param {Array<Object>} props.deviceCustomAccessTwoFa - Employee assignments for 2FA.
 * @param {Function} props.setDeviceCustomAccessTwoFa - Updates 2FA employee assignments.
 * @param {Array<Object>} props.deviceDepartmentTwoFa - Department assignments for 2FA.
 * @param {Function} props.setDeviceDepartmentTwoFa - Updates 2FA department assignments.
 * @param {string} props.zoneNameProps - Current zone name.
 * @param {number|string} props.zoneIdProps - Current zone identifier.
 * @param {Function} props.setModified - Updates modified state.
 * @param {Array<number>} props.defaultDistance - Default distance configuration.
 * @param {Object} props.data - Device form data model.
 * @param {Function} props.t - Translation function.
 * @param {Function} props.dispatch - Redux dispatch function.
 * @param {Function} props.navigate - Navigation handler.
 * @param {Function} props.setEmptyFields - Updates validation state.
 * @param {number|string|undefined} props.id - Device identifier.
 * @param {Function} props.setData - Updates device form model.
 * @param {boolean} props.modified - Indicates whether data was modified.
 *
 * @returns {Object} Hook response object.
 * @returns {Function} returns.handleUpdate - Executes device update flow.
 */
export const UpdateDevicesApp = ({
                                     setSteps,
                                     handleError,
                                     deviceCustomAccess,
                                     setDeviceCustomAccess,
                                     deviceDepartment,
                                     setDeviceDepartment,
                                     deviceCustomAccessTwoFa,
                                     setDeviceCustomAccessTwoFa,
                                     deviceDepartmentTwoFa,
                                     setDeviceDepartmentTwoFa,
                                     zoneNameProps,
                                     zoneIdProps,
                                     setModified,
                                     defaultDistance,
                                     data,
                                     t,
                                     dispatch,
                                     navigate,
                                     setEmptyFields,
                                     id,
                                     setData,
                                     modified
                                 }) => {

    useLayoutEffect(()=>{
        const stepArray = Object.values(STEPS_UPDATE_DEVICE)
            .filter(step => step?.TRANS && typeof step.INDEX === "number")
            .sort((a,b) => a.INDEX - b.INDEX)
            .map(step => t(step.TRANS))
        setSteps(stepArray)
    },[t])

    /**
     * Main device update handler.
     *
     * Handles:
     * - Device update request.
     * - Section validation.
     * - Employee assignments.
     * - Department assignments.
     * - Navigation after success.
     */
    const handleUpdate = async () => {

        const {
            data: d,
            options: o,
            security: s,
            authentication: a,
            relay: r,
            unoFa,
            dosFa
        } = data;

        const body = {
            name: d.deviceName,
            deviceId: d.idDevice,
            ip: d.ip,
            mac: d.mac,
            serialNumber: d.sn,
            description:d.description,
            deviceAccessTypeId: d.accessType,
            deviceTypeId: d.deviceType,
            statusId: d.statusValue,
            zoneId: d.zoneName === '' ? null : d.zoneName,
            password:
                d.password &&
                typeof d.password === 'string' &&
                d.password.trim() !== ''
                    ? d.password
                    : null,
            modeId: d.modeId,
            deviceRestrictionId: d.deviceRestrictionId,

            antiPassBackId: s.antiPassBackType,
            antiPassBackTime: s.antiPassBackTime,
            antiPassBackAlertById: s.antiPassBackAlert,
            escortId: s.escortMode,
            validationNumber: s.validationNumber,
            syncTime: s.syncTime,
            requestSignature: s.requestSignature,
            requestSignatureAlways: s.requestSignatureAlways,

            authenticationMethod: a.authentication,
            bluetoothReader: a.bluetooth,
            rfidReader: a.smartCard,
            facialRecognition: a.facialRecognition,
            liveness: a.liveness,
            fingerprintReader: a.fingerprint,
            pin: a.pin,
            qrCodeReader: a.qr,
            firebaseId: null,
            readAfter: a.readAfter,
            cameraTypeId: a.cameraTypeId,
            enableDistance: a.enableDistance,
            distance:
                a.enableDistance === true
                    ? a.distance
                    : defaultDistance[d.deviceType],
            consecutiveOpeningsAllowed: a.consecutiveOpeningsAllowed,

            timeZoneId: o.timeZoneValue,
            synchronizeSelfie: o.synchorizeSelfies,
            playSound: o.playSounds,
            showLogo: o.showLogo,
            showUserList: o.showUserList,
            showWeapon: o.showWeapon,
            showVehicle: o.showVehicle,
            showLed: o.showLed,
            showAccessDialog: o.showAccessDialog,
            showLastAccesses: o.showLastAccess,
            printer: o.printer,

            delayToOpen: r.delayToOpen,
            keepOpenRelay: r.keepOpenRelay,

            enableRelay1FA: unoFa.enableRelay1FA,
            openRelayForAll1FA: unoFa.openRelayForAll1FA,

            enableRelay2FA: dosFa.enableRelay2FA,
            openRelayForAll2FA: dosFa.openRelayForAll2FA
        };


        /**
         * Processes employee or department access assignments.
         *
         * @async
         *
         * @param {Object} config - Assignment configuration.
         * @param {Array<Object>} config.list - Resources to process.
         * @param {Function} config.getResourceId - Extracts resource identifier.
         * @param {number} config.authenticationMode - Authentication mode identifier.
         * @param {Function} config.action - Redux action dispatcher.
         * @param {number|string} config.deviceId - Device identifier.
         * @param {Function} config.onSuccessClear - Clears local state after success.
         */
        const processAccess = async ({
                                         list,
                                         getResourceId,
                                         authenticationMode,
                                         action,
                                         deviceId,
                                         onSuccessClear
                                     }) => {
            if (!list || list.length === 0) return;

            const validList = list.filter(e => {
                const id = getResourceId(e);
                return id !== null && id !== undefined;
            });

            if (validList.length === 0) return;

            const payload = validList.map(e => ({
                deviceId,
                resourceId: getResourceId(e),
                authenticationMode
            }));

            const {payload: {data: {code, success}}} = await dispatch(action(payload));

            if (success) {
                setModified(true);
                onSuccessClear();
            } else {
                errorMsg(code);
            }
        };

        setEmptyFields(false);

        const stateToValidate = {
            data: d,
            options: o,
            security: s,
            authentication: a,
            relay: r
        };

        const sectionsToValidate = [1, 2, 3, 4, 5, 6];

        const allSectionsValid = sectionsToValidate.every(section =>
            isValid(section, "submit", stateToValidate)
        );

        if (!allSectionsValid) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        try {
            let response;

            if (id) {
                body.id = id;
                response = await dispatch(UpdateDeviceApi(body));
            }

            const { success, code, data: responseData } = response.payload.data;
            if (!success) {
                errorMsg(code);
                return;
            }

            const deviceIdFinal = responseData?.id;

            await processAccess({
                list: deviceCustomAccess,
                getResourceId: e => e.userId,
                authenticationMode: AUTHENTICATION_MODEL.ONE_FA,
                action: CreateEmployee,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceCustomAccess([])
            });

            await processAccess({
                list: deviceDepartment,
                getResourceId: e => e.id,
                authenticationMode: AUTHENTICATION_MODEL.ONE_FA,
                action: CreateDepartment,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceDepartment([])
            });

            await processAccess({
                list: deviceCustomAccessTwoFa,
                getResourceId: e => e.userId,
                authenticationMode: AUTHENTICATION_MODEL.TWO_FA,
                action: CreateEmployee,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceCustomAccessTwoFa([])
            });

            await processAccess({
                list: deviceDepartmentTwoFa,
                getResourceId: e => e.id,
                authenticationMode: AUTHENTICATION_MODEL.TWO_FA,
                action: CreateDepartment,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceDepartmentTwoFa([])
            });

            toast.success(
                t('device_updated_successfully').toUpperCase()
            );

            navigate(
                zoneNameProps !== undefined
                    ? routes.zone.details + zoneIdProps
                    : id
                        ? setModified(true)
                        : routes.devices.list
            );

        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        getData()
    }, []);

    useEffect(() => {
        if (modified) {
            getData()
        }
        setModified(false)
    }, [modified])

    const getData = () => {

        if (zoneNameProps !== undefined) {
            setData(prev => ({
                ...prev,
                data: {
                    ...prev.data,
                    zoneName: zoneIdProps,
                    zone: zoneIdProps
                }
            }));
            return;
        }

        if (id !== undefined) {
            dispatch(GetDeviceDetails(id)).then(({ payload: { data: { data } } }) => {

                setData(prev => ({
                    ...prev,

                    data: {
                        ...prev.data,
                        deviceName: data?.name,
                        idDevice: data?.deviceId,
                        deviceId: data?.id,
                        zoneName: data?.zoneId,
                        zone: data?.zoneId,
                        deviceType: data?.deviceTypeId,
                        statusValue: data?.statusId,
                        ip: data?.ip,
                        description: data?.description,
                        password: data?.password,
                        accessType: data?.deviceAccessTypeId,
                        sn: data?.serialNumber,
                        mac: data?.mac,
                        modeId: data?.modeId,
                        deviceRestrictionId: data?.deviceRestrictionId,
                    },

                    options: {
                        ...prev.options,
                        timeZoneValue: data?.timeZoneId,
                        showWeapon: data?.showWeapon,
                        showVehicle: data?.showVehicle,
                        showLed: data?.showLed,
                        showAccessDialog: data?.showAccessDialog,
                        showLastAccess: data?.showLastAccesses,
                        showLogo: data?.showLogo,
                        showUserList: data?.showUserList,
                        synchorizeSelfies: data?.synchronizeSelfie,
                        playSounds: data?.playSound,
                        printer: data?.printer,
                    },

                    security: {
                        ...prev.security,
                        antiPassBackType: data?.antiPassBackId,
                        antiPassBackTime: data?.antiPassBackTime,
                        antiPassBackAlert: data?.antiPassBackAlertById,
                        escortMode: data?.escortId,
                        syncTime: data?.syncTime,
                        requestSignature: data?.requestSignature,
                        requestSignatureAlways: data?.requestSignatureAlways,
                        validationNumber: data?.validationNumber,
                    },

                    authentication: {
                        ...prev.authentication,
                        authentication: data?.authenticationMethod,
                        readAfter: data?.readAfter,
                        smartCard: data?.rfidReader,
                        facialRecognition: data?.facialRecognition,
                        qr: data?.qrCodeReader,
                        pin: data?.pin,
                        fingerprint: data?.fingerprintReader,
                        bluetooth: data?.bluetoothReader,
                        liveness: data?.liveness,
                        cameraTypeId: data?.cameraTypeId,
                        enableDistance: data?.enableDistance,
                        distance: data?.distance,
                        consecutiveOpeningsAllowed: data?.consecutiveOpeningsAllowed,
                    },

                    relay: {
                        ...prev.relay,
                        delayToOpen: data?.delayToOpen,
                        keepOpenRelay: data?.keepOpenRelay,
                    },

                    unoFa: {
                        ...prev.unoFa,
                        openRelayForAll1FA: data?.openRelayForAll1FA,
                        enableRelay1FA: data?.enableRelay1FA

                    },

                    dosFa: {
                        ...prev.dosFa,
                        openRelayForAll2FA: data?.openRelayForAll2FA,
                        enableRelay2FA: data?.enableRelay2FA
                    }
                }));
            });
        }
    };

    return {
        handleUpdate
    }

}
